import * as ReactDOM from 'react-dom';
import {
  ACTION_CHECKOUT_ADDONS_GO_TO_NEXT_SECTION,
  ACTION_CHECKOUT_ADDONS_OPEN_ADDON,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { Alert, Button, Divider, Typography } from 'omakase';
import { AmplitudeSendEventProps, withAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { Container, Grid, ScrollListNavigationPanel } from 'ui';
import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { SupplementGroupState } from 'types';
import {
  Theme,
  WithStyles,
  WithTheme,
  createStyles,
  withStyles,
  withTheme,
} from '@material-ui/core/styles';
import { getIsCompactView, getSelectionString } from '@tock/shared/utils/supplementsUtils';
import {
  getTicketOptionInfoMap,
  isOptionGroupValid,
  segmentOptionGroups,
  segmentOptionGroupsAndOptions,
  sortOptionOrder,
} from '@tock/shared/utils/option';
import { logger } from '@tock/logging';
import { scrollToChild } from '@tock/shared/utils/scroll';
import BaseComponent from '@tock/shared/components/BaseComponent';
import BottomContainer from 'components/ui/BottomContainer';
import ConfirmOptionsDialog from 'components/business/addons/ConfirmOptionsDialog';
import MenuFilter, {
  getTopLevelOptionGroupsWithFilter,
  getTopLevelOptionsWithFilter,
  getUnionSetOfOptionGroupsToDisplay,
} from 'components/business/menu/MenuFilter';
import ModifierDialog from 'components/business/addons/ModifierDialog';
import SupplementGroupCompact, {
  GroupComponentType,
} from 'components/business/addons/SupplementGroupCompact';
import SupplementGroupSummaryCard from 'components/business/addons/SupplementGroupSummaryCard';
import _ from 'lodash';
import clsx from 'clsx';
import pluralize from 'pluralize';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: theme.colors.bgDefault,
      padding: 0,
      width: '100%',
      zIndex: theme.zIndex.appBar,
    },
    stickyContainer: {
      top: `calc(env(safe-area-inset-top) + 59px)`, // height of header
      position: 'sticky',
      zIndex: theme.zIndex.speedDial,
      [theme.breakpoints.up('md')]: {
        top: 'env(safe-area-inset-top)',
      },
    },
    tableNumberInput: {
      marginBottom: theme.spacing(1),
    },
    currentSelection: {
      marginBottom: theme.spacing(5),
      '&:first-child': {
        marginTop: theme.spacing(3),
        [theme.breakpoints.up('sm')]: {
          '&': {
            marginTop: theme.spacing(4),
          },
        },
      },
    },
    name: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  });

interface OwnProps extends WithTheme, WithStyles<typeof styles> {
  business: consumer.IBusinessConfig;
  calculatedPrice: core.PurchaseOrder;
  checkoutOrigin?: 'TABLESIDE' | 'TABLESIDE_QR_TABLE';
  onConfirm(): void;
  onSubmitOrder(): Promise<unknown>;
  onUpdatePrice(): Promise<unknown>;
  optionOrders: core.IOptionOrder[];
  optionPrices: core.IPrice[];
  partyOptionOrders?: core.IOptionOrder[];
  partySize: number;
  patron?: core.IPatron;
  selectOptions(options: core.IOptionOrder[] | null): void;
  setSupplementGroupStates(supplementGroupStates: SupplementGroupState[]): void;
  supplementGroupStates: SupplementGroupState[];
  ticketOption: core.ITicketOption[];
  ticketOptionGroup: core.ITicketOptionGroup[];
  ticketType: core.ITicketType;
}

type Props = OwnProps & IWithContextProps & AmplitudeSendEventProps;

interface State {
  showConfirmOptionsDialog: boolean;
  submitting: boolean;
  modificationData?: {
    optionOrders: core.IOptionOrder[];
    optionOrdersToEdit?: core.IOptionOrder[];
    ticketOption: core.ITicketOption;
    ticketOptionGroup: core.ITicketOptionGroup;
    modifierOptionGroups: core.ITicketOptionGroup[];
    patron?: core.IPatron;
  };
  optionGroupsInView: { [optionGroupId: number]: boolean };
  searchText: string;
  currentTicketOptionGroupIndex: number;
}

class AddonSelection extends BaseComponent<Props, State> {
  scrollToRef: string | null;

  state: State = {
    showConfirmOptionsDialog: false,
    submitting: false,
    optionGroupsInView: {},
    searchText: '',
    currentTicketOptionGroupIndex: this.getSelectedSupplementGroupIndex() ?? 0,
  };

  componentDidUpdate() {
    if (this.scrollToRef !== null && this.refs[this.scrollToRef]) {
      const node = ReactDOM.findDOMNode(this.refs[this.scrollToRef]) as HTMLElement;
      const scrollOptions = {
        offset: -48,
      };
      scrollToChild(document.body, node, scrollOptions);
      scrollToChild(document.documentElement, node, scrollOptions);
      this.scrollToRef = null;
    }
  }

  handleClearSelections(
    options: core.IOptionOrder[],
    ticketOptionGroupId: number,
    optionGroupIndex: number
  ) {
    // Clears all selections for this option group,
    // and advances the user to the next section.
    // Only available for optional option groups.
    this.handleChangeQuantity(options);
    const supplementGroupStates = _.cloneDeep(this.props.supplementGroupStates);
    const editedSupplementGroupState = _.find(supplementGroupStates, (state) => {
      return state.id === ticketOptionGroupId;
    });

    if (editedSupplementGroupState) {
      editedSupplementGroupState.isEditing = false;
      editedSupplementGroupState.isSkipped = true;
      this.props.setSupplementGroupStates(supplementGroupStates);
    }

    this.handleContinue(optionGroupIndex);
  }

  handleChangeQuantity(orders: core.IOptionOrder[]) {
    const optionInfoMap = getTicketOptionInfoMap(this.props);
    const optionOrderSort = sortOptionOrder(optionInfoMap);
    const updatedOrders = _.sortBy(orders, ...optionOrderSort) as core.IOptionOrder[];
    _.forEach(updatedOrders, (order) => {
      if (order.modifierOption) {
        order.modifierOption = _.sortBy(
          order.modifierOption as core.OptionOrder[],
          ...optionOrderSort
        );
      }
    });
    this.props.selectOptions(updatedOrders);
  }

  handleOpenModifierModal(
    ticketOption: core.ITicketOption,
    ticketOptionGroup: core.ITicketOptionGroup,
    modifierOptionGroups: core.ITicketOptionGroup[],
    defaultQuantity: number,
    ticketOptionListIndex: number,
    ticketOptionGroupListIndex: number
  ) {
    const { patron, amplitudeSendEvent } = this.props;
    const optionOrders = _.times(defaultQuantity, () => {
      return {
        optionId: ticketOption.id,
        numPurchased: 1,
        patron,
        backingMode: ticketOption.backingMode,
        parentGroupId: ticketOptionGroup.id,
      };
    });

    amplitudeSendEvent(ACTION_CHECKOUT_ADDONS_OPEN_ADDON, {
      addOnId: ticketOption.id || undefined,
      addOnName: ticketOption.name || undefined,
      addOnListIndex: ticketOptionListIndex,
      addOnGroupId: ticketOptionGroup.id || undefined,
      addOnGroupName: ticketOptionGroup.name || undefined,
      addOnGroupListIndex: ticketOptionGroupListIndex,
    });

    this.setState({
      modificationData: {
        optionOrders,
        optionOrdersToEdit: undefined,
        ticketOption,
        ticketOptionGroup,
        modifierOptionGroups,
        patron,
      },
    });
  }

  handleRemoveOptionOrders(optionOrders: core.IOptionOrder[]) {
    const updatedOrders = _.filter(
      this.props.optionOrders,
      (order) => !_.some(optionOrders, (orderToRemove) => orderToRemove === order)
    );
    this.handleChangeQuantity(updatedOrders);
    setTimeout(this.props.onUpdatePrice, 0);
  }

  handleEditOptionOrders(optionOrders: core.IOptionOrder[]) {
    const {
      patron,
      ticketOption: allTicketOptions,
      ticketOptionGroup: allTicketOptionGroups,
    } = this.props;

    const optionId = optionOrders[0].optionId;
    const ticketOption = _.find(allTicketOptions, (option) => option.id === optionId);
    const ticketOptionGroup = _.find(
      allTicketOptionGroups,
      (group) => group.id === ticketOption?.ticketOptionGroupId
    );
    const modifierOptionGroups = _.filter(
      allTicketOptionGroups,
      (group) => group.parentTicketOptionId === ticketOption?.id
    );
    if (!ticketOption || !ticketOptionGroup) {
      logger.error('Invalid state, option not found', optionId);
      return;
    }
    this.setState({
      modificationData: {
        optionOrders: [...optionOrders],
        optionOrdersToEdit: [...optionOrders],
        ticketOption,
        ticketOptionGroup,
        modifierOptionGroups,
        patron,
      },
    });
  }

  handleCloseModiferDialog() {
    this.setState({
      modificationData: undefined,
    });
  }

  handleUpdateOptionWithModifier(optionOrders: core.IOptionOrder[]) {
    const ordersToEdit = this.state.modificationData?.optionOrdersToEdit;
    const updatedOrders = _.filter(
      this.props.optionOrders,
      (order) => !_.some(ordersToEdit, (orderToRemove) => orderToRemove === order)
    );
    this.handleChangeQuantity(_.concat(updatedOrders, optionOrders || []));
    this.handleCloseModiferDialog();
    setTimeout(this.props.onUpdatePrice, 0);
  }

  handleChangeOptionWithModifiers(orders: core.IOptionOrder[]) {
    if (!this.state.modificationData) {
      logger.error('Invalid state, modification window not open', orders);
      return;
    }
    this.setState({
      modificationData: {
        ...this.state.modificationData,
        optionOrders: orders,
      },
    });
  }

  handleConfirmOptionsDialog() {
    this.props.onUpdatePrice().then(() => {
      this.setState({
        showConfirmOptionsDialog: true,
      });
    });
  }

  handleCloseConfirmOptionsDialog(ticketOptionGroupId?: number) {
    this.setState({
      showConfirmOptionsDialog: false,
    });
    if (ticketOptionGroupId) {
      this.handleEdit(ticketOptionGroupId);
    }
  }

  handleEdit(ticketOptionGroupId: number) {
    const supplementGroupStates = _.cloneDeep(this.props.supplementGroupStates);
    const editedSupplementGroupState = _.find(supplementGroupStates, (state) => {
      return state.id === ticketOptionGroupId;
    });
    if (editedSupplementGroupState) {
      editedSupplementGroupState.isEditing = !editedSupplementGroupState.isEditing;
    }
    this.props.setSupplementGroupStates(supplementGroupStates);
  }

  handleSaveChoices(ticketOptionGroupId: number, optionGroupIndex: number) {
    const supplementGroupStates = _.cloneDeep(this.props.supplementGroupStates);
    const editedSupplementGroupState = _.find(supplementGroupStates, (state) => {
      return state.id === ticketOptionGroupId;
    });

    if (editedSupplementGroupState) {
      editedSupplementGroupState.isCompleted = true;
      editedSupplementGroupState.isSkipped = false;
      editedSupplementGroupState.isEditing = false;
    }
    this.props.setSupplementGroupStates(supplementGroupStates);
    this.handleContinue(optionGroupIndex);

    this.scrollToRef = `supplementGroup-${ticketOptionGroupId}`;
  }

  handleContinue(optionGroupIndex: number) {
    const nextTicketOptionGroupInfo = this.getNextTicketOptionGroupInfo();
    if (nextTicketOptionGroupInfo) {
      this.setState({
        currentTicketOptionGroupIndex: this.state.currentTicketOptionGroupIndex + 1,
      });
      this.props.amplitudeSendEvent(ACTION_CHECKOUT_ADDONS_GO_TO_NEXT_SECTION, {
        addOnGroupListIndex: optionGroupIndex + 1,
        addOnGroupId: nextTicketOptionGroupInfo.id,
        addOnGroupName: nextTicketOptionGroupInfo.name,
      });
    } else if (this.getGroupComponentType() === 'DEFAULT') {
      this.handleConfirm();
    }
  }

  handleConfirm() {
    if (this.isValid()) {
      this.props.onConfirm();
    }
  }

  handleSubmitOrder() {
    if (this.isValid()) {
      this.setState({ submitting: true });
      this.props
        .onSubmitOrder()
        .then(() => this.setState({ submitting: false }))
        .catch(() => this.setState({ submitting: false }));
    }
  }

  handleChangeSearchText(value: string) {
    this.setState(() => {
      return { searchText: value };
    });
  }

  isValid() {
    const {
      context,
      ticketOptionGroup,
      ticketType,
      partySize,
      ticketOption: ticketOptions,
      optionOrders,
      partyOptionOrders,
      calculatedPrice,
    } = this.props;
    const { topLevelOptionGroups } = segmentOptionGroups(ticketOptionGroup, ticketType, context);
    return _.every(topLevelOptionGroups, (ticketOptionGroup) => {
      const options = _.filter(ticketOptions, {
        ticketOptionGroupId: ticketOptionGroup.id,
        backingMode: ticketOptionGroup.backingMode,
      });
      return isOptionGroupValid(
        [...optionOrders, ...(partyOptionOrders || [])],
        options,
        partySize,
        ticketOptionGroup,
        { waiveMinimumRequirements: !!calculatedPrice.visitEditToken }
      );
    });
  }

  getSelectedSupplementGroupIndex() {
    // A supplement group is opened if it is the next pristine group,
    // or if the customer is editing their choices in that section
    const selectedSupplementGroupIndex = _.findIndex(this.props.supplementGroupStates, (state) => {
      return state.isEditing;
    });

    if (selectedSupplementGroupIndex >= 0) {
      return selectedSupplementGroupIndex;
    }

    const notCompletedIndex = _.findIndex(this.props.supplementGroupStates, (state) => {
      return state.isCompleted === false && state.isSkipped === false;
    });
    if (notCompletedIndex >= 0) {
      return notCompletedIndex;
    }
    return undefined;
  }

  /**
   * Returns the ticket group id to go "back" to, if it exists. Only relevant in coursed view.
   */
  getBackTicketOptionGroupId(): number | undefined {
    const { currentTicketOptionGroupIndex } = this.state;
    const { supplementGroupStates } = this.props;
    if (currentTicketOptionGroupIndex > 0) {
      return supplementGroupStates[currentTicketOptionGroupIndex - 1].id;
    }
    return undefined;
  }

  /**
   * Returns a boolean indicating whether the current section's back button should be enabled. Only relevant in coursed view.
   */
  isBackEnabled() {
    return !!this.getBackTicketOptionGroupId();
  }

  handleClickBack() {
    this.setState({
      currentTicketOptionGroupIndex: Math.max(this.state.currentTicketOptionGroupIndex - 1, 0),
    });
  }

  /**
   * Returns the name of the next section (ticket option group) to be seen, or undefined when on the last section. Only relevant in the coursed view.
   */
  getNextTicketOptionGroupInfo(): { id: number; name: string } | undefined {
    const { supplementGroupStates } = this.props;
    const nextIndex = this.state.currentTicketOptionGroupIndex + 1;
    if (nextIndex >= 0 && nextIndex < supplementGroupStates.length) {
      const { id, name } = supplementGroupStates[nextIndex];
      return { id, name };
    }
    return undefined;
  }

  getIsCompactView() {
    const {
      context: { isFeatureEnabled },
      ticketType,
    } = this.props;
    return getIsCompactView({
      ticketType: ticketType,
      isFeatureEnabled,
    });
  }

  getGroupComponentType() {
    const isCompactView = this.getIsCompactView();
    return isCompactView ? 'COMPACT' : 'DEFAULT';
  }

  render() {
    const {
      business,
      calculatedPrice,
      checkoutOrigin,
      classes,
      context,
      optionOrders,
      optionPrices,
      partyOptionOrders,
      partySize,
      patron,
      supplementGroupStates,
      theme,
      ticketOption,
      ticketOptionGroup,
      ticketType,
    } = this.props;
    const { searchText } = this.state;

    const {
      optionsWithModifiers,
      topLevelOptionGroups,
      modifierOptionGroups,
      topLevelTicketOptions,
    } = segmentOptionGroupsAndOptions(ticketOptionGroup, ticketOption, ticketType, context);
    const isCompactView = this.getIsCompactView();
    const groupComponentType: GroupComponentType = this.getGroupComponentType();

    const topLevelOptionGroupsWithFilter = getTopLevelOptionGroupsWithFilter(
      searchText,
      topLevelOptionGroups
    );
    const topLevelOptionsWithFilter = getTopLevelOptionsWithFilter(
      searchText,
      topLevelTicketOptions
    );

    const topLevelOptionGroupsToShow = getUnionSetOfOptionGroupsToDisplay(
      topLevelOptionGroups,
      topLevelOptionGroupsWithFilter,
      topLevelOptionsWithFilter
    );
    const ticketOptionPrices = _.map(ticketOption, (ticketOption) => {
      return _.find(optionPrices, {
        id: ticketOption.id,
        backingMode: ticketOption.backingMode,
      }) as core.IPrice;
    });

    const itemCount = _.size(optionOrders);
    const isConfirmButtonEnabled = this.isValid();
    const { modificationData } = this.state;

    return (
      <>
        {isCompactView && (
          <Container className={clsx(classes.container, classes.stickyContainer)}>
            <MenuFilter
              value={
                _.filter(
                  topLevelOptionGroupsToShow,
                  (optionGroup) =>
                    !!optionGroup?.id && !!this.state.optionGroupsInView[optionGroup.id]
                )?.[0]?.id ?? undefined
              }
              optionGroups={topLevelOptionGroupsToShow}
              onChangeSearch={_.debounce(this.handleChangeSearchText, 200)}
              // Own height, plus header height
              offset={113 + 59}
              // Own height, plus a fudge factor:
              offsetMd={113 + theme.spacing(1)}
            />
          </Container>
        )}
        {topLevelOptionGroupsToShow.length === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <Alert severity="info">
                <p>
                  No results for “{searchText}”<br />
                  Please check your spelling and try another search, or browse for more options
                </p>
              </Alert>
            </Grid>
          </Grid>
        )}

        {groupComponentType === 'DEFAULT' && !!optionOrders.length && (
          <>
            <div className={classes.currentSelection}>
              <SupplementGroupSummaryCard
                onClickEdit={this.handleConfirmOptionsDialog}
                editButtonType="View"
              >
                <>
                  <Typography component="h4" variant="body01Strong" className={classes.name}>
                    Your selections
                  </Typography>
                  <Typography variant="body02" component="p" color="fgSubtle">
                    {getSelectionString(optionOrders, topLevelTicketOptions).join(', ')}
                  </Typography>
                </>
              </SupplementGroupSummaryCard>
            </div>
            <Divider />
          </>
        )}
        {_.map(topLevelOptionGroupsToShow, (ticketOptionGroup, index) => {
          if (!ticketOptionGroup.id) {
            return null;
          }

          const ticketOptionGroupIsMatched =
            topLevelOptionGroupsWithFilter.includes(ticketOptionGroup);

          const ticketOptionsWithFilter = (
            ticketOptionGroupIsMatched ? ticketOption : topLevelOptionsWithFilter
          ).filter((ticketOption) => ticketOption.ticketOptionGroupId === ticketOptionGroup.id);
          if (ticketOptionsWithFilter.length < 1) {
            return null;
          }

          const supplementGroupState = _.find(supplementGroupStates, (state) => {
            return state.id === ticketOptionGroup.id;
          });

          if (!supplementGroupState) {
            return null;
          }

          let isOpen = groupComponentType === 'COMPACT';
          if (groupComponentType === 'DEFAULT') {
            isOpen = index === this.state.currentTicketOptionGroupIndex;
          }

          if (groupComponentType === 'DEFAULT' && !isOpen) {
            return null;
          }

          const componentArgs = isCompactView
            ? {}
            : {
                ref: `supplementGroup-${ticketOptionGroup?.id}`,
                collapse: false,
                moveSectionBySection: true,
              };

          return (
            <ScrollListNavigationPanel
              key={ticketOptionGroup?.id ?? undefined}
              value={ticketOptionGroup?.id ?? undefined}
              inView={{
                threshold: 0.05,
                rootMargin: '-117px 0px 0px 0px',
                onChange: (isInView, value) =>
                  value
                    ? this.setState((state) => ({
                        optionGroupsInView: {
                          ...state.optionGroupsInView,
                          [value]: isInView,
                        },
                      }))
                    : undefined,
              }}
            >
              <SupplementGroupCompact
                {...componentArgs}
                isOpen={isOpen}
                isCompleted={supplementGroupState.isCompleted}
                isSkipped={supplementGroupState.isSkipped}
                key={ticketOptionGroup.id as number}
                partySize={partySize}
                optionOrders={optionOrders}
                partyOptionOrders={partyOptionOrders}
                optionsWithModifiers={optionsWithModifiers}
                modifierOptionGroups={modifierOptionGroups}
                ticketOptions={ticketOptionsWithFilter}
                ticketOptionGroup={ticketOptionGroup}
                ticketOptionPrices={ticketOptionPrices}
                ticketType={ticketType}
                onChangeQuantity={this.handleChangeQuantity}
                onClearSelections={(selectedOptions, ticketOptionGroupId) =>
                  this.handleClearSelections(selectedOptions, ticketOptionGroupId, index)
                }
                onEdit={this.handleEdit}
                onSaveChoices={(ticketOptionGroupId) =>
                  this.handleSaveChoices(ticketOptionGroupId, index)
                }
                onOpenModifierModal={(
                  ticketOption: core.ITicketOption,
                  ticketOptionGroup: core.ITicketOptionGroup,
                  modifierOptionGroups: core.ITicketOptionGroup[],
                  ticketOptionListIndex: number,
                  defaultQuantity: number
                ) =>
                  this.handleOpenModifierModal(
                    ticketOption,
                    ticketOptionGroup,
                    modifierOptionGroups,
                    defaultQuantity,
                    ticketOptionListIndex,
                    index
                  )
                }
                patron={patron}
                isBackEnabled={this.isBackEnabled()}
                onClickBack={this.handleClickBack}
                nextSectionInfo={this.getNextTicketOptionGroupInfo()}
                totalPrice={calculatedPrice?.totalPrice}
              />
            </ScrollListNavigationPanel>
          );
        })}
        {isCompactView && (
          <BottomContainer>
            <Button
              variant="primary"
              fullWidth
              size="large"
              onClick={this.handleConfirmOptionsDialog}
              data-testid="supplement-page-view-order"
            >
              View order
              {itemCount > 0 && (
                <span>
                  &nbsp; &middot; &nbsp;
                  {pluralize('item', itemCount, true)}
                </span>
              )}
            </Button>
          </BottomContainer>
        )}

        {modificationData && (
          <ModifierDialog
            isOpen={!!modificationData}
            isEditing={!!modificationData.optionOrdersToEdit}
            onClose={this.handleCloseModiferDialog}
            onComplete={this.handleUpdateOptionWithModifier}
            ticketOption={modificationData.ticketOption}
            ticketOptionGroup={modificationData.ticketOptionGroup}
            optionOrders={modificationData.optionOrders}
            otherOptionOrders={_.filter(
              optionOrders,
              (order) =>
                !_.some(
                  modificationData.optionOrdersToEdit,
                  (orderToRemove) => orderToRemove === order
                )
            )}
            modifierOptionGroups={modificationData.modifierOptionGroups}
            allTicketOptions={ticketOption}
            ticketOptionPrices={optionPrices}
            ticketType={ticketType}
            partySize={partySize}
            onChangeQuantity={this.handleChangeOptionWithModifiers}
            patron={patron}
          />
        )}

        {this.state.showConfirmOptionsDialog && (
          <ConfirmOptionsDialog
            isOpen={this.state.showConfirmOptionsDialog}
            onClose={this.handleCloseConfirmOptionsDialog}
            onComplete={this.handleConfirm}
            onSubmitOrder={this.handleSubmitOrder}
            confirmDisabled={!isConfirmButtonEnabled}
            submitting={this.state.submitting}
            business={business}
            purchase={calculatedPrice}
            patron={patron}
            ticketType={ticketType}
            optionOrders={optionOrders}
            partyOptionOrders={partyOptionOrders}
            extraChargeDetails={ticketType.extraChargeDetails as core.ExtraChargeDetails}
            ticketOptions={ticketOption}
            ticketOptionPrices={optionPrices}
            topLevelTicketOptionGroups={topLevelOptionGroups}
            onEditOptionOrder={this.handleEditOptionOrders}
            onRemoveOptionOrder={this.handleRemoveOptionOrders}
            checkoutOrigin={checkoutOrigin}
            showPriceInformation={isCompactView}
            showSelectionsBySection={!isCompactView}
          />
        )}
      </>
    );
  }
}

export default withTheme(withStyles(styles)(withContext(withAmplitudeSendEvent(AddonSelection))));
